import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { SmallLazoTeamUser } from "@/models/SmallLazoTeamUser";
import { EmployeeAssignedTask, EmployeeAssignedTasksFilters } from "@/types/EmployeeAssignedTask";
import { EmployeeStats } from "@/types/EmployeeStats";
import { LazoArea } from "@/types/LazoArea";
import { LazoJobTitle } from "@/types/LazoJobTitle";
import { LazoTeamOfCompany } from "@/types/LazoTeamOfCompany";
import { LazoTeamPageFilters } from "@/types/LazoTeamPageFilters";
import { LazoEmployeeAssociatedProduct } from "@/types/ProductAssociatedLazoUser";
import { UpdateLazoEmployee } from "@/types/UpdateLazoEmployee";
import { UserDeactivationBlockers } from "@/types/UserDeactivationBlockers";

class LazoTeamService extends GenericCrudServiceV2<SmallLazoTeamUser> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  getAllAreas(): Promise<ApiResponseV2<LazoArea[]>> {
    return HttpClientV2.get(`${this.path}/areas`);
  }

  getJobTitles(areaExternalId?: string, isInternal?: boolean): Promise<ApiResponseV2<LazoJobTitle[]>> {
    let url = `${this.path}/job-titles?isInternal=${isInternal ?? true}`;
    if (areaExternalId) url += `&areaExternalId=${areaExternalId}`;
    return HttpClientV2.get(url);
  }

  getLazoTeamPaged(page: number, pageSize: number, filters?: LazoTeamPageFilters): Promise<ApiResponseV2<IPageV2<SmallLazoTeamUser>>> {
    return HttpClientV2.post(`${this.path}/paged?page=${page}&pageSize=${pageSize}`, filters);
  }

  getEmployeeAssociatedProducts(
    externalId: string,
    page: number,
    pageSize: number,
    filters: { search: string; module: string[] },
    orderBy?: string,
    direction?: string
  ): Promise<ApiResponseV2<IPageV2<LazoEmployeeAssociatedProduct>>> {
    let url = `${this.path}/employee/associated-products?page=${page}&pageSize=${pageSize}&lazoUserExternalId=${externalId}`;
    if (orderBy) url += `&orderBy=${orderBy}`;
    if (direction) url += `&direction=${direction}`;
    return HttpClientV2.post(url, filters);
  }

  updateLazoEmployee(externalId: string, dto: UpdateLazoEmployee): Promise<ApiResponseV2<IPageV2<LazoEmployeeAssociatedProduct>>> {
    return HttpClientV2.put(`${this.path}/employee/${externalId}/update`, dto);
  }

  getLazoTeamOfCompany(companyExternalId: string, page: number, pageSize: number): Promise<ApiResponseV2<IPageV2<LazoTeamOfCompany>>> {
    return HttpClientV2.get(`${this.path}/of-company/${companyExternalId}/paged?page=${page}&pageSize=${pageSize}`);
  }

  hasActiveFlows(lazoUserExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.get(`${this.path}/employee/${lazoUserExternalId}/has-active-flows`);
  }

  getDeactivationBlockers(lazoUserExternalId: string): Promise<ApiResponseV2<UserDeactivationBlockers>> {
    return HttpClientV2.get(`${this.path}/employee/${lazoUserExternalId}/deactivation-blockers`);
  }

  getEmployeeAssignedTasks(
    externalId: string,
    page: number,
    pageSize: number,
    filters: EmployeeAssignedTasksFilters,
    orderBy?: string,
    direction?: string
  ): Promise<ApiResponseV2<IPageV2<EmployeeAssignedTask>>> {
    let url = `${this.path}/employee/${externalId}/assigned-tasks?page=${page}&pageSize=${pageSize}`;
    if (orderBy) url += `&orderBy=${orderBy}`;
    if (direction) url += `&direction=${direction}`;
    return HttpClientV2.post(url, filters);
  }

  getEmployeeStats(lazoUserExternalId: string, activeWorkflowsOnly: boolean = true): Promise<ApiResponseV2<EmployeeStats>> {
    return HttpClientV2.get(`${this.path}/employee/${lazoUserExternalId}/stats?activeWorkflowsOnly=${activeWorkflowsOnly}`);
  }
}

export default new LazoTeamService("/api/lazo-team");
